import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ArrowClockwise, Code, EnvelopeSimple, FileJs, FilePy, Warning } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { devWarn } from "@/lib/logger";

/**
 * /admin/code-health — Super-admin dashboard fed by GET /api/_admin/complexity.
 *
 * Renders:
 *  - Two side-by-side cards (Python + JavaScript) with summary metrics and the
 *    live top-N cyclomatic-complexity offenders.
 *  - Rank distribution strips (A→F) with counts inline.
 *  - Refresh button that bypasses the 5-min server cache (`?fresh=true`).
 *
 * Access: super_admin only. Anyone else sees a locked panel — server also
 * returns 403 so the UI just needs to be honest about it.
 */

const RANKS = ["A", "B", "C", "D", "E", "F"];
const RANK_BUCKETS = {
  A: { label: "A ≤5", swatch: "bg-emerald-500" },
  B: { label: "B ≤10", swatch: "bg-lime-500" },
  C: { label: "C ≤20", swatch: "bg-amber-400" },
  D: { label: "D ≤30", swatch: "bg-orange-500" },
  E: { label: "E ≤40", swatch: "bg-rose-500" },
  F: { label: "F >40", swatch: "bg-black" },
};

function rankPillClass(rank) {
  switch (rank) {
    case "A":
      return "bg-emerald-500 text-black border-emerald-700";
    case "B":
      return "bg-lime-400 text-black border-lime-700";
    case "C":
      return "bg-amber-300 text-black border-amber-700";
    case "D":
      return "bg-orange-400 text-white border-orange-700";
    case "E":
      return "bg-rose-500 text-white border-rose-800";
    case "F":
      return "bg-black text-white border-black";
    default:
      return "bg-white text-black border-zinc-400";
  }
}

function RankStrip({ counts, total, testId }) {
  // Draw a proportional stacked bar of A→F.
  const safe = total > 0 ? total : 1;
  return (
    <div data-testid={testId}>
      <div className="flex h-3 w-full border border-black overflow-hidden">
        {RANKS.map((r) => {
          const c = counts[r] || 0;
          if (!c) return null;
          const pct = (c / safe) * 100;
          return (
            <div
              key={r}
              className={RANK_BUCKETS[r].swatch}
              style={{ width: `${pct}%` }}
              title={`${RANK_BUCKETS[r].label} — ${c}`}
              data-testid={`${testId}-seg-${r}`}
            />
          );
        })}
      </div>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] label-mono">
        {RANKS.map((r) => (
          <div key={r} className="flex items-center gap-1">
            <span className={`inline-block w-2 h-2 ${RANK_BUCKETS[r].swatch} border border-black`} />
            <span>{RANK_BUCKETS[r].label}</span>
            <span className="font-bold text-black">{counts[r] || 0}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function OffendersTable({ rows, sideLabel, testIdPrefix }) {
  if (!rows?.length) {
    return (
      <div className="border border-black p-4 text-sm text-zinc-500" data-testid={`${testIdPrefix}-empty`}>
        No functions found.
      </div>
    );
  }
  return (
    <table className="w-full border-collapse border border-black" data-testid={`${testIdPrefix}-table`}>
      <thead className="bg-black text-white text-[10px] uppercase tracking-widest">
        <tr>
          <th className="text-left px-2 py-2 w-8">#</th>
          <th className="text-left px-2 py-2">Function</th>
          <th className="text-left px-2 py-2 w-[45%]">File</th>
          <th className="text-right px-2 py-2 w-16">CC</th>
          <th className="text-center px-2 py-2 w-14">Rank</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, idx) => (
          <tr
            key={`${row.file}:${row.line}:${row.name}`}
            className="border-t border-black hover:bg-zinc-100"
            data-testid={`${testIdPrefix}-row-${idx}`}
          >
            <td className="px-2 py-2 label-mono">{idx + 1}</td>
            <td className="px-2 py-2 font-mono text-[12px] truncate max-w-[220px]" title={row.name}>
              {row.name}
            </td>
            <td className="px-2 py-2 font-mono text-[11px] text-zinc-700 truncate" title={`${row.file}:${row.line}`}>
              {row.file}
              <span className="text-zinc-400">:{row.line}</span>
            </td>
            <td className="px-2 py-2 text-right font-bold" data-testid={`${testIdPrefix}-cc-${idx}`}>
              {row.complexity}
            </td>
            <td className="px-2 py-2 text-center">
              <span
                className={`inline-block border px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${rankPillClass(row.rank)}`}
                title={`${sideLabel} rank`}
              >
                {row.rank}
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function SideCard({ title, Icon, side, testIdPrefix }) {
  const summary = side?.summary || { count: 0, max: 0, average: 0, rank_counts: {} };
  return (
    <section className="brut-card p-5 flex flex-col gap-4" data-testid={`${testIdPrefix}-card`}>
      <header className="flex items-center justify-between border-b border-black pb-3">
        <div className="flex items-center gap-2">
          <Icon size={22} weight="bold" />
          <h2 className="font-chivo font-black text-2xl uppercase tracking-tighter leading-none">{title}</h2>
        </div>
        <div className="text-[11px] label-mono text-right">
          <div>
            <span className="font-bold text-black" data-testid={`${testIdPrefix}-total`}>{summary.count}</span> functions
          </div>
          <div>
            max <span className="font-bold text-black" data-testid={`${testIdPrefix}-max`}>{summary.max}</span> · avg{" "}
            <span className="font-bold text-black" data-testid={`${testIdPrefix}-avg`}>{summary.average}</span>
          </div>
        </div>
      </header>

      <RankStrip counts={summary.rank_counts || {}} total={summary.count} testId={`${testIdPrefix}-ranks`} />

      <div>
        <div className="label-mono mb-2">Top offenders</div>
        <OffendersTable rows={side?.top_offenders || []} sideLabel={title} testIdPrefix={testIdPrefix} />
      </div>

      {side?.errors?.length > 0 && (
        <div className="border border-rose-500 bg-rose-50 p-3 text-[12px]" data-testid={`${testIdPrefix}-errors`}>
          <div className="flex items-center gap-1 font-bold text-rose-800 uppercase tracking-widest text-[10px]">
            <Warning size={12} /> {side.errors.length} parse/compute error(s)
          </div>
          <ul className="mt-1 font-mono text-[11px] space-y-1">
            {side.errors.slice(0, 5).map((e) => (
              <li key={`${e.file}:${e.error}`} className="truncate">
                {e.file}: {e.error}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

const TOP_CHOICES = [5, 10, 20, 50];

function fmtDate(iso) {
  if (!iso) return "never";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return String(iso);
  }
}

export default function CodeHealth() {
  const { user } = useAuth();
  const [top, setTop] = useState(10);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [digestState, setDigestState] = useState(null);
  const [digestBusy, setDigestBusy] = useState(false);

  const load = useCallback(async (fresh = false, nextTop = top) => {
    setLoading(true);
    setError(null);
    try {
      const { data: resp } = await api.get("/_admin/complexity", {
        params: { top: nextTop, ...(fresh ? { fresh: "true" } : {}) },
      });
      setData(resp);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || "Failed to load complexity data";
      setError(msg);
      if (e.response?.status !== 403) toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [top]);

  const loadDigestState = useCallback(async () => {
    try {
      const { data: resp } = await api.get("/_admin/complexity/digest/state");
      setDigestState(resp);
    } catch (e) {
      // Non-fatal: banner just falls back to "never".
      devWarn("Failed to load digest state:", e?.message || e);
    }
  }, []);

  const sendDigestNow = useCallback(async () => {
    if (!window.confirm("Send Code Health digest email to all super_admins now?")) return;
    setDigestBusy(true);
    try {
      const { data: resp } = await api.post("/_admin/complexity/digest/send-now");
      if (resp.status === "sent" || resp.status === "partial") {
        toast.success(`Digest ${resp.status} to ${resp.sent_ok}/${resp.recipients?.length} super_admins`);
      } else if (resp.status === "baseline_recorded") {
        toast.info("Baseline snapshot recorded — nothing to diff against yet. Click again to send the first real digest.");
      } else if (resp.status === "no_signal") {
        toast.info("No offenders above rank D crossed the threshold — no email sent.");
      } else if (resp.status === "no_recipients") {
        toast.warning("No super_admin recipients found.");
      } else {
        toast.error(`Digest ${resp.status}: ${resp.error || "unknown"}`);
      }
      loadDigestState();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Send failed");
    } finally {
      setDigestBusy(false);
    }
  }, [loadDigestState]);

  useEffect(() => {
    if (user?.role === "super_admin") {
      load(false, top);
      loadDigestState();
    }
  }, [user?.role]);

  const cacheLabel = useMemo(() => {
    if (!data?.cache) return "";
    const ts = new Date(data.cache.generated_at * 1000);
    const hit = data.cache.hit ? "cached" : "fresh";
    return `${hit} · generated ${ts.toLocaleTimeString()}`;
  }, [data]);

  if (user && user.role !== "super_admin") {
    return (
      <div className="brut-card p-6" data-testid="code-health-denied">
        <div className="label-mono">Code health</div>
        <div className="mt-1 font-chivo font-black text-2xl uppercase">Super admin only</div>
        <div className="text-sm text-zinc-600 mt-2">
          The live complexity report is intentionally restricted — it shells out to `radon` and `node`.
        </div>
      </div>
    );
  }

  return (
    <div data-testid="code-health-page">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="label-mono">Repo insights</div>
          <h1 className="font-chivo font-black text-4xl md:text-5xl uppercase tracking-tighter leading-none">
            Code health
          </h1>
          <div className="mt-1 text-sm text-zinc-600 max-w-2xl">
            Live cyclomatic complexity report from `radon cc` (Python) + a `@babel/parser` walker (JS/JSX).
            Cached server-side for 5 minutes.
            <span className="ml-2 label-mono" data-testid="code-health-cache-info">{cacheLabel}</span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <label className="label-mono flex items-center gap-2" htmlFor="code-health-top">
            <span>Top</span>
            <select
              id="code-health-top"
              data-testid="code-health-top-select"
              value={top}
              onChange={(e) => {
                const n = Number(e.target.value);
                setTop(n);
                load(false, n);
              }}
              disabled={loading}
              className="border border-black px-2 py-1 text-[12px] bg-white uppercase tracking-widest disabled:opacity-50"
            >
              {TOP_CHOICES.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>

          <button
            onClick={() => load(true, top)}
            disabled={loading}
            data-testid="code-health-refresh"
            className="bg-black text-white border border-black px-4 py-2 uppercase text-xs tracking-widest font-bold disabled:opacity-60 hover:translate-x-[2px] hover:translate-y-[2px] hover:brut-shadow-sm transition-transform duration-100 flex items-center gap-2"
          >
            <ArrowClockwise size={14} weight="bold" className={loading ? "animate-spin" : ""} />
            {loading ? "Running…" : "Recompute"}
          </button>

          <button
            onClick={sendDigestNow}
            disabled={digestBusy}
            data-testid="code-health-send-digest"
            title="Snapshot + diff + email all super_admins now"
            className="bg-white text-black border border-black px-4 py-2 uppercase text-xs tracking-widest font-bold disabled:opacity-60 hover:bg-black hover:text-white transition-colors duration-100 flex items-center gap-2"
          >
            <EnvelopeSimple size={14} weight="bold" />
            {digestBusy ? "Sending…" : "Send digest now"}
          </button>
        </div>
      </div>

      {digestState && (
        <div className="brut-card p-3 mb-4 flex flex-wrap gap-x-6 gap-y-1 text-[12px]" data-testid="code-health-digest-state">
          <div className="label-mono">Weekly digest</div>
          <div>
            <span className="text-zinc-500">schedule</span>{" "}
            <span className="font-bold">{digestState.schedule}</span>
          </div>
          <div>
            <span className="text-zinc-500">threshold</span>{" "}
            <span className="font-bold">{digestState.threshold}</span>
          </div>
          <div>
            <span className="text-zinc-500">last sent</span>{" "}
            <span className="font-bold" data-testid="code-health-digest-last-sent">
              {fmtDate(digestState.last_sent_at)}
            </span>
          </div>
          <div>
            <span className="text-zinc-500">last status</span>{" "}
            <span className="font-bold">{digestState.last_status || "—"}</span>
          </div>
          <div>
            <span className="text-zinc-500">snapshots</span>{" "}
            <span className="font-bold">{digestState.snapshot_count ?? 0}</span>
          </div>
        </div>
      )}

      {error && (
        <div className="border border-rose-600 bg-rose-50 p-4 mb-6" data-testid="code-health-error">
          <div className="flex items-center gap-2 font-bold text-rose-800 uppercase tracking-widest text-xs">
            <Warning size={14} /> Failed to load
          </div>
          <div className="text-sm mt-1 font-mono">{error}</div>
        </div>
      )}

      {!data && !error && (
        <div className="brut-card p-8 flex items-center gap-3 text-sm" data-testid="code-health-loading">
          <Code size={20} weight="bold" />
          <span>Loading complexity report…</span>
        </div>
      )}

      {data && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <SideCard title="Python" Icon={FilePy} side={data.python} testIdPrefix="code-health-py" />
          <SideCard title="JavaScript" Icon={FileJs} side={data.javascript} testIdPrefix="code-health-js" />
        </div>
      )}

      {data && (
        <p className="mt-6 text-[12px] text-zinc-600 max-w-3xl label-mono" data-testid="code-health-docs">
          {data.docs}
        </p>
      )}
    </div>
  );
}
